// Ingest from camera drive (Context.HeliconflowCameraIngest.md A-3).
//
// The PYXIS 6K records BRAW + an automatic HD H.264 proxy to a camera-attached USB-C SSD. A PA plugs the drive in, picks its folder
// once, and this page sweeps the NEW proxies into Helicon, each flagged "original is BRAW". No installed software: the browser's
// directory picker (File System Access API — Chrome / Edge).
//
// READ-ONLY on the drive: the page only lists folders and reads files (values() / getFile()). It never writes, moves, renames or
// deletes, and never opens anything but *.mp4 / *.MP4 — BRAW is invisible to it.
//
// The API's ingest/check is the truth for what is already in Helicon — never this page's memory — so a re-run after an interruption
// uploads only what is missing. The picked folder handle is kept in the browser's own IndexedDB (Owner call 5): the next session
// re-asks permission, not a fresh pick. Nothing about the drive is stored server-side.

import { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../lib/api';
import { multipartUpload } from '../lib/multipartUpload';
import { useAuth } from '../context/AuthContext';

// Owner call 4: the raw-footage upload roles MINUS external users. The API enforces the same set (uniform 404).
const INGEST_ROLES = ['Editor', 'Producer', 'Post Lead', 'Post_Lead', 'Power User', 'Power_User', 'Admin'];
const MAX_PARALLEL = 2;
const LAST_CLIENT_KEY = 'helicon.ingest.clientId';
const CHECK_CHUNK = 5000;

const isIngestName = name => /\.mp4$/i.test(name) && !name.startsWith('._');

function fmtSize(b) {
  const n = Number(b) || 0;
  if (n >= 1024 ** 3) return `${(n / 1024 ** 3).toFixed(1)} GB`;
  if (n >= 1024 ** 2) return `${(n / 1024 ** 2).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(n / 1024))} KB`;
}
const errText = err => err?.response?.data?.error || err?.message || 'Upload failed';
const keyOf = f => `${f.name}|${f.size}`;

// ─── The browser's own storage for the folder handle (IndexedDB — handles are structured-cloneable) ───────────────────────────────

const IDB_NAME = 'helicon-camera-ingest', IDB_STORE = 'handles', IDB_KEY = 'drive';
function idb(mode, fn) {
  return new Promise((resolve, reject) => {
    const open = indexedDB.open(IDB_NAME, 1);
    open.onupgradeneeded = () => open.result.createObjectStore(IDB_STORE);
    open.onerror = () => reject(open.error);
    open.onsuccess = () => {
      const tx = open.result.transaction(IDB_STORE, mode);
      const r = fn(tx.objectStore(IDB_STORE));
      tx.oncomplete = () => { open.result.close(); resolve(r?.result); };
      tx.onerror = () => { open.result.close(); reject(tx.error); };
    };
  });
}
const loadHandle = () => idb('readonly', s => s.get(IDB_KEY)).catch(() => null);
const saveHandle = h => idb('readwrite', s => s.put(h, IDB_KEY)).catch(() => {});

// Recursive READ-ONLY walk: list entries, read .mp4 metadata. Hidden folders (.Trashes, .Spotlight-V100 …) are skipped.
async function scanDirectory(dir, prefix = '', out = []) {
  for await (const entry of dir.values()) {
    if (entry.kind === 'directory') {
      if (!entry.name.startsWith('.')) await scanDirectory(entry, `${prefix}${entry.name}/`, out);
    } else if (isIngestName(entry.name)) {
      const file = await entry.getFile();
      out.push({ name: entry.name, size: file.size, path: `${prefix}${entry.name}`, handle: entry });
    }
  }
  return out;
}

const STATUS_COPY = {
  exists:      'Already in Helicon',
  in_progress: 'Upload in progress elsewhere — skipped this run',
  failed:      'An earlier upload failed verification — resolve it in Raw Footage',
};

export default function CameraIngest() {
  const { user } = useAuth();
  const canIngest = !user?.isExternal && (user?.roles || []).some(r => INGEST_ROLES.includes(r));
  const supported = typeof window !== 'undefined' && typeof window.showDirectoryPicker === 'function';

  const [handle, setHandle]           = useState(null);
  const [permission, setPermission]   = useState(null);   // 'granted' | 'prompt' | 'denied'
  const [scanning, setScanning]       = useState(false);
  const [scanError, setScanError]     = useState('');
  const [drive, setDrive]             = useState(null);    // { files, results: Map key → check result, scannedAt, dupNames }
  const [clients, setClients]         = useState([]);
  const [clientId, setClientId]       = useState(() => localStorage.getItem(LAST_CLIENT_KEY) || '');
  const [braw, setBraw]               = useState(true);
  const [items, setItems]             = useState([]);      // upload rows { key, name, size, path, state, progress, error, note }
  const [showSkipped, setShowSkipped] = useState(false);
  const fileByKey = useRef(new Map());
  const running   = useRef(false);
  // key → footageId whose bytes are in but whose /confirm failed: Retry re-confirms that upload rather than sending the file again
  // (its pending row would otherwise read as "in progress elsewhere" for an hour)
  const unconfirmed = useRef(new Map());

  useEffect(() => {
    if (!canIngest) return;
    api.get('/api/clients').then(r => setClients(r.data.filter(c => c.isActive))).catch(() => {});
  }, [canIngest]);

  // The remembered drive: re-ask permission only (a click is needed for the prompt, so it is a button, not automatic).
  useEffect(() => {
    if (!canIngest || !supported) return;
    loadHandle().then(async h => {
      if (!h) return;
      setHandle(h);
      try { setPermission(await h.queryPermission({ mode: 'read' })); } catch { setPermission('prompt'); }
    });
  }, [canIngest, supported]);

  const uploading = items.some(i => i.state === 'uploading' || i.state === 'verifying' || i.state === 'queued');
  useEffect(() => {
    if (!uploading) return undefined;
    const warn = e => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [uploading]);

  const check = useCallback(async (files) => {
    const results = new Map();
    for (let i = 0; i < files.length; i += CHECK_CHUNK) {
      const chunk = files.slice(i, i + CHECK_CHUNK);
      const { data } = await api.post('/api/footage/ingest/check', { clientId: clientId || null, files: chunk.map(f => ({ name: f.name, size: f.size })) });
      data.results.forEach(r => results.set(keyOf(r), r));
    }
    return results;
  }, [clientId]);

  const scan = useCallback(async (dir) => {
    setScanning(true); setScanError('');
    try {
      const found = await scanDirectory(dir);
      // the same file twice on the drive (same name + size, two folders) is one file
      const unique = new Map();
      const dupNames = [];
      for (const f of found) { if (unique.has(keyOf(f))) dupNames.push(f.path); else unique.set(keyOf(f), f); }
      const files = [...unique.values()].sort((a, b) => a.path.localeCompare(b.path));
      fileByKey.current = unique;
      const results = files.length ? await check(files) : new Map();
      setDrive({ files, results, scannedAt: new Date(), dupNames });
    } catch (err) {
      setScanError(err?.name === 'NotAllowedError' ? 'The browser did not allow reading the drive. Reconnect it and try again.' : errText(err));
    } finally {
      setScanning(false);
    }
  }, [check]);

  async function pickFolder() {
    try {
      const dir = await window.showDirectoryPicker({ id: 'helicon-camera-drive', mode: 'read' });
      setHandle(dir); setPermission('granted'); setItems([]);
      await saveHandle(dir);
      await scan(dir);
    } catch (err) {
      if (err?.name !== 'AbortError') setScanError(errText(err));
    }
  }
  async function reconnect() {
    try {
      const p = await handle.requestPermission({ mode: 'read' });
      setPermission(p);
      if (p === 'granted') await scan(handle);
    } catch (err) { setScanError(errText(err)); }
  }

  function chooseClient(id) {
    setClientId(id);
    if (id) localStorage.setItem(LAST_CLIENT_KEY, id);
  }

  const patch = (key, p) => setItems(list => list.map(i => (i.key === key ? { ...i, ...p } : i)));

  async function confirm(key, footageId) {
    patch(key, { state: 'verifying', progress: 100, error: undefined });
    const body = { footageId };
    if (braw) body.originalExt = 'braw';
    let data;
    try {
      ({ data } = await api.post('/api/footage/confirm', body));
    } catch (err) {
      unconfirmed.current.set(key, footageId);
      patch(key, { state: 'failed', error: `Uploaded, but Helicon did not confirm it: ${errText(err)}` });
      return;
    }
    unconfirmed.current.delete(key);
    if (data?.verified === false) patch(key, { state: 'failed', error: data?.verification?.error || 'Verification failed — the uploaded file does not match the drive.' });
    else patch(key, { state: 'done', note: data?.isPossibleDuplicate ? `Stored as a possible duplicate${data.duplicateOfName ? ` of ${data.duplicateOfName}` : ''}` : undefined });
  }

  async function uploadOne(f, result) {
    const key = keyOf(f);
    patch(key, { state: 'uploading', progress: 0, error: undefined });
    let footageId;
    try {
      const file = await f.handle.getFile();
      if (file.size !== f.size) throw new Error('The file changed on the drive since the scan — rescan.');
      const startBody = { clientId };
      if (result.status === 'stale') startBody.replaceStaleFootageIds = result.staleFootageIds;
      const up = multipartUpload({
        file, api,
        endpoints: { start: '/api/footage/multipart/start', signPart: '/api/footage/multipart/sign-part', complete: '/api/footage/multipart/complete', abort: '/api/footage/multipart/abort' },
        startBody, idFieldName: 'footageId',
        onProgress: (loaded, total) => patch(key, { progress: Math.round((loaded / total) * 100) }),
      });
      ({ footageId } = await up.promise);
    } catch (err) {
      patch(key, { state: 'failed', error: errText(err) });
      return;
    }
    await confirm(key, footageId);
  }

  // Upload `keys` — always re-checked against the API first, so only files that are still missing go up. `fresh` = a new batch (the
  // Upload button: "N of M" counts this batch); Retry keeps the batch.
  async function upload(keys, { fresh = false } = {}) {
    if (running.current || !clientId) return;
    running.current = true;
    try {
      const reconfirm = keys.filter(k => unconfirmed.current.has(k));
      for (const k of reconfirm) await confirm(k, unconfirmed.current.get(k));
      keys = keys.filter(k => !reconfirm.includes(k));
      if (!keys.length) return;
      const files = keys.map(k => fileByKey.current.get(k)).filter(Boolean);
      let results;
      try { results = await check(files); } catch (err) { setScanError(errText(err)); return; }
      const todo = files.filter(f => ['new', 'stale'].includes(results.get(keyOf(f))?.status));
      setItems(list => {
        const keep = fresh ? [] : list.filter(i => !keys.includes(i.key));
        return [...keep, ...files.map(f => {
          const r = results.get(keyOf(f));
          const go = todo.includes(f);
          return { key: keyOf(f), name: f.name, size: f.size, path: f.path, state: go ? 'queued' : 'skipped', progress: 0, error: go ? undefined : (STATUS_COPY[r?.status] || 'Skipped'), note: go && r?.nameTaken ? 'A different file already uses this name — it will be stored with a -1 suffix' : undefined };
        })];
      });
      const queue = [...todo];
      const worker = async () => { while (queue.length) { const f = queue.shift(); await uploadOne(f, results.get(keyOf(f))); } };
      await Promise.all(Array.from({ length: Math.min(MAX_PARALLEL, queue.length) }, worker));
    } finally {
      running.current = false;
      // refresh the picture from the API — the truth — once the run ends
      if (drive?.files?.length) { try { const results = await check(drive.files); setDrive(d => ({ ...d, results })); } catch { /* keep the old picture */ } }
    }
  }

  if (!canIngest) {
    return <div style={{ padding: 40, color: 'var(--tx-muted)', fontFamily: 'var(--font)' }} data-testid="ingest-not-found">Not found.</div>;
  }

  const results = drive?.results || new Map();
  const statusOf = f => results.get(keyOf(f))?.status;
  const newFiles = (drive?.files || []).filter(f => ['new', 'stale'].includes(statusOf(f)));
  const skipped  = (drive?.files || []).filter(f => !['new', 'stale'].includes(statusOf(f)));
  const newBytes = newFiles.reduce((s, f) => s + f.size, 0);
  const counted  = items.filter(i => i.state !== 'skipped');
  const done     = counted.filter(i => i.state === 'done').length;
  const failed   = counted.filter(i => i.state === 'failed');
  const box = { background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 10, padding: 18, marginBottom: 16 };

  return (
    <div style={{ height: '100vh', overflowY: 'auto', background: 'var(--bg-app)', fontFamily: 'var(--font)', color: 'var(--tx-primary)' }}>
      <div style={{ padding: '20px 28px', maxWidth: 980 }}>
        <div style={{ fontSize: 12, marginBottom: 6 }}><Link to="/footage" style={{ color: 'var(--tx-muted)' }}>← Raw Footage</Link></div>
        <h1 style={{ fontSize: 20, fontWeight: 700, margin: 0 }}>Ingest from camera drive</h1>
        <div style={{ fontSize: 12, color: 'var(--tx-muted)', marginTop: 4, maxWidth: 720 }}>
          Plug in the camera SSD, pick its folder, and the new MP4 proxies go up to Helicon. Read-only: this page never changes,
          moves or deletes anything on the drive, and ignores everything that is not an .mp4 (the BRAW originals stay where they are).
        </div>

        {!supported ? (
          <div style={{ ...box, marginTop: 18 }} data-testid="ingest-unsupported">
            <b>This page needs Google Chrome or Microsoft Edge on a computer.</b>
            <div style={{ fontSize: 13, color: 'var(--tx-muted)', marginTop: 6 }}>
              It reads the camera drive through the browser's folder picker, which this browser does not offer. Open Helicon in Chrome or Edge and come back to this page.
            </div>
          </div>
        ) : (
          <>
            {/* 1 — the drive */}
            <div style={{ ...box, marginTop: 18 }}>
              <div style={{ fontWeight: 700, marginBottom: 10 }}>1 · Camera drive</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                {handle && <span style={{ fontSize: 13 }} data-testid="ingest-drive-name">📁 {handle.name}</span>}
                {handle && permission !== 'granted' && (
                  <button className="btn-primary" style={{ fontSize: 13 }} onClick={reconnect} data-testid="ingest-reconnect">Reconnect “{handle.name}”</button>
                )}
                {handle && permission === 'granted' && (
                  <button className="btn-ghost" style={{ fontSize: 13 }} disabled={scanning || uploading} onClick={() => scan(handle)} data-testid="ingest-rescan">↻ Rescan</button>
                )}
                <button className={handle ? 'btn-ghost' : 'btn-primary'} style={{ fontSize: 13 }} disabled={scanning || uploading} onClick={pickFolder} data-testid="ingest-pick">
                  {handle ? 'Choose a different folder…' : 'Choose camera drive folder…'}
                </button>
              </div>
              {handle && permission !== 'granted' && <div style={{ fontSize: 12, color: 'var(--tx-muted)', marginTop: 8 }}>The browser asks again each session before this page may read the drive.</div>}
              {scanning && <div style={{ fontSize: 13, marginTop: 10 }} data-testid="ingest-scanning">Scanning the drive…</div>}
              {scanError && <div style={{ fontSize: 13, marginTop: 10, color: 'var(--danger, #e5534b)' }} data-testid="ingest-error">{scanError}</div>}
              {drive && !scanning && (
                <div style={{ marginTop: 12, fontSize: 13 }} data-testid="ingest-summary">
                  <b data-testid="ingest-new-count">{newFiles.length} new file{newFiles.length === 1 ? '' : 's'}</b>
                  {newFiles.length > 0 && <> · <span data-testid="ingest-new-size">{fmtSize(newBytes)}</span></>}
                  <span style={{ color: 'var(--tx-muted)' }}> · {drive.files.length} MP4 on the drive · {skipped.length} skipped</span>
                  {drive.dupNames.length > 0 && <div style={{ fontSize: 12, color: 'var(--tx-muted)', marginTop: 4 }}>{drive.dupNames.length} file{drive.dupNames.length === 1 ? ' appears' : 's appear'} twice on the drive — counted once.</div>}
                </div>
              )}
            </div>

            {/* 2 — where it goes */}
            {drive && (
              <div style={box}>
                <div style={{ fontWeight: 700, marginBottom: 10 }}>2 · Project and format</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                  <label style={{ fontSize: 13 }}>
                    Project{' '}
                    <select value={clientId} onChange={e => chooseClient(e.target.value)} disabled={uploading} data-testid="ingest-client" style={{ marginLeft: 6, background: 'var(--bg-input)', border: '1px solid var(--border-mid)', borderRadius: 8, padding: '8px 12px', color: 'var(--tx-primary)', fontSize: 13, fontFamily: 'var(--font)', minWidth: 220 }}>
                      <option value="">— choose a project —</option>
                      {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  </label>
                  <label style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <input type="checkbox" checked={braw} onChange={e => setBraw(e.target.checked)} disabled={uploading} data-testid="ingest-braw" />
                    Original is BRAW
                  </label>
                </div>
                <div style={{ fontSize: 12, color: 'var(--tx-muted)', marginTop: 8 }}>
                  With “Original is BRAW” on, each upload is marked as a proxy, so the cut-list download points Premiere at the <code>.braw</code> of the same name. Don't rename the files.
                </div>
                <div style={{ marginTop: 14, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                  <button className="btn-primary" style={{ fontSize: 13 }} disabled={!clientId || uploading || !newFiles.length} onClick={() => upload(newFiles.map(keyOf), { fresh: true })} data-testid="ingest-upload">
                    {newFiles.length ? `Upload ${newFiles.length} new file${newFiles.length === 1 ? '' : 's'} (${fmtSize(newBytes)})` : 'Nothing new to upload'}
                  </button>
                  {!clientId && newFiles.length > 0 && <span style={{ fontSize: 12, color: 'var(--tx-muted)' }}>Choose a project first.</span>}
                </div>
              </div>
            )}

            {/* 3 — progress */}
            {counted.length > 0 && (
              <div style={box}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                  <div style={{ fontWeight: 700 }} data-testid="ingest-progress">{done} of {counted.length} uploaded</div>
                  {failed.length > 0 && !uploading && (
                    <button className="btn-ghost" style={{ fontSize: 12 }} onClick={() => upload(failed.map(i => i.key))} data-testid="ingest-retry-all">Retry {failed.length} failed</button>
                  )}
                </div>
                {counted.map(i => (
                  <div key={i.key} data-testid="ingest-item" data-state={i.state} data-name={i.name} style={{ padding: '8px 0', borderTop: '1px solid var(--border)', fontSize: 13 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={i.path}>{i.name}</span>
                      <span style={{ color: 'var(--tx-muted)', fontSize: 12 }}>{fmtSize(i.size)}</span>
                      <span style={{ width: 120, fontSize: 12, textAlign: 'right' }}>
                        {i.state === 'queued' && 'Waiting'}
                        {i.state === 'uploading' && `${i.progress}%`}
                        {i.state === 'verifying' && 'Verifying…'}
                        {i.state === 'done' && '✓ Uploaded'}
                        {i.state === 'failed' && <span style={{ color: 'var(--danger, #e5534b)' }}>Failed</span>}
                      </span>
                      {i.state === 'failed' && !uploading && <button className="btn-ghost" style={{ fontSize: 12 }} onClick={() => upload([i.key])} data-testid="ingest-retry">Retry</button>}
                    </div>
                    {(i.state === 'uploading' || i.state === 'verifying') && (
                      <div style={{ height: 4, background: 'var(--bg-card2)', borderRadius: 2, marginTop: 6 }}>
                        <div style={{ height: 4, width: `${i.progress}%`, background: 'var(--violet)', borderRadius: 2 }} />
                      </div>
                    )}
                    {i.error && <div style={{ fontSize: 12, color: 'var(--danger, #e5534b)', marginTop: 4 }}>{i.error}</div>}
                    {i.note && <div style={{ fontSize: 12, color: 'var(--tx-muted)', marginTop: 4 }}>{i.note}</div>}
                  </div>
                ))}
              </div>
            )}

            {/* already in Helicon / skipped, and why */}
            {drive && skipped.length > 0 && (
              <div style={box}>
                <button className="btn-ghost" style={{ fontSize: 12 }} onClick={() => setShowSkipped(v => !v)} data-testid="ingest-skipped-toggle">
                  {showSkipped ? '▾' : '▸'} Skipped ({skipped.length})
                </button>
                {showSkipped && skipped.map(f => {
                  const r = results.get(keyOf(f));
                  return (
                    <div key={keyOf(f)} data-testid="ingest-skipped" data-name={f.name} data-status={r?.status} style={{ padding: '6px 0', borderTop: '1px solid var(--border)', fontSize: 13, display: 'flex', gap: 10 }}>
                      <span style={{ flex: 1 }} title={f.path}>{f.name}</span>
                      <span style={{ color: 'var(--tx-muted)', fontSize: 12 }}>
                        {STATUS_COPY[r?.status] || r?.status}
                        {r?.match && <> — {r.match.where}{r.match.archived ? ', archived' : ''}{r.match.fileName !== f.name ? ` (as ${r.match.fileName})` : ''}</>}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
